"use client"

import { useCallback, useMemo, useState, type KeyboardEvent } from "react"

import { PIPELINE_SEO_DRUGS } from "@/lib/public-tools/pipeline-seo-drugs"

import styles from "./tracker-suggested-drugs.module.css"

const SEO_GENERIC_KEYS = new Set(
  PIPELINE_SEO_DRUGS.map((d) => d.generic.trim().toLowerCase()),
)

type SuggestedDrug = {
  generic: string
  indication: string
  /** Sampled 2026-07-14 via GET /api/public/trial-pipeline?drugs=<generic> */
  trialCount: number
  capped: boolean
}

type TabId = "popular" | "metabolic" | "oncology" | "neurology"

const TABS: { id: TabId; label: string }[] = [
  { id: "popular", label: "Popular" },
  { id: "metabolic", label: "Metabolic" },
  { id: "oncology", label: "Oncology" },
  { id: "neurology", label: "Neurology" },
]

// Trial counts sampled 2026-07-14 (local /api/public/trial-pipeline). Count 16
// means composer cap hit, display as 16+. Replace with live metrics later.
const RAW_BY_TAB: Record<TabId, SuggestedDrug[]> = {
  popular: [
    { generic: "semaglutide", indication: "obesity, T2D", trialCount: 16, capped: true },
    { generic: "tirzepatide", indication: "obesity, T2D", trialCount: 16, capped: true },
    { generic: "pembrolizumab", indication: "oncology", trialCount: 16, capped: true },
    { generic: "lecanemab", indication: "Alzheimer's", trialCount: 12, capped: false },
    { generic: "empagliflozin", indication: "heart failure", trialCount: 16, capped: true },
    { generic: "retatrutide", indication: "obesity", trialCount: 16, capped: true },
  ],
  metabolic: [
    { generic: "semaglutide", indication: "obesity, T2D", trialCount: 16, capped: true },
    { generic: "tirzepatide", indication: "obesity, T2D", trialCount: 16, capped: true },
    { generic: "retatrutide", indication: "obesity", trialCount: 16, capped: true },
    { generic: "orforglipron", indication: "obesity, oral", trialCount: 16, capped: true },
    { generic: "cagrilintide", indication: "obesity", trialCount: 16, capped: true },
    { generic: "dapagliflozin", indication: "T2D, heart failure", trialCount: 16, capped: true },
  ],
  oncology: [
    { generic: "pembrolizumab", indication: "oncology", trialCount: 16, capped: true },
    { generic: "nivolumab", indication: "oncology", trialCount: 16, capped: true },
    { generic: "sotorasib", indication: "NSCLC", trialCount: 16, capped: true },
    { generic: "adagrasib", indication: "NSCLC", trialCount: 15, capped: false },
    {
      generic: "trastuzumab deruxtecan",
      indication: "breast cancer",
      trialCount: 16,
      capped: true,
    },
    {
      generic: "sacituzumab govitecan",
      indication: "breast cancer",
      trialCount: 16,
      capped: true,
    },
  ],
  neurology: [
    { generic: "lecanemab", indication: "Alzheimer's", trialCount: 12, capped: false },
    { generic: "donanemab", indication: "Alzheimer's", trialCount: 14, capped: false },
    { generic: "atogepant", indication: "migraine", trialCount: 16, capped: true },
    {
      generic: "xanomeline-trospium",
      indication: "schizophrenia",
      trialCount: 16,
      capped: true,
    },
  ],
}

function drugsForTab(tab: TabId): SuggestedDrug[] {
  return RAW_BY_TAB[tab].filter((d) =>
    SEO_GENERIC_KEYS.has(d.generic.trim().toLowerCase()),
  )
}

function formatTrialCount(drug: SuggestedDrug): string {
  if (drug.capped) return "16+ TRIALS"
  return `${drug.trialCount} TRIALS`
}

export type TrackerSuggestedDrugsProps = {
  onSearchSelect: (displayTerm: string) => void | Promise<void>
  /** White raised plate sibling — tabs use pill/scope-marker voice. */
  raised?: boolean
  /** Drop the default top margin (when search is rendered separately). */
  flushTop?: boolean
}

export function TrackerSuggestedDrugs({
  onSearchSelect,
  raised = false,
  flushTop = false,
}: TrackerSuggestedDrugsProps) {
  const [activeTab, setActiveTab] = useState<TabId>("popular")

  const visibleDrugs = useMemo(() => drugsForTab(activeTab), [activeTab])

  const focusTab = useCallback((tabId: TabId) => {
    document.getElementById(`tracker-tab-${tabId}`)?.focus()
  }, [])

  const handleTabKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>, tabId: TabId) => {
      const currentIndex = TABS.findIndex((tab) => tab.id === tabId)
      if (currentIndex < 0) return

      let nextIndex: number | null = null
      switch (event.key) {
        case "ArrowRight":
          nextIndex = (currentIndex + 1) % TABS.length
          break
        case "ArrowLeft":
          nextIndex = (currentIndex - 1 + TABS.length) % TABS.length
          break
        case "Home":
          nextIndex = 0
          break
        case "End":
          nextIndex = TABS.length - 1
          break
        default:
          return
      }

      event.preventDefault()
      const nextTab = TABS[nextIndex]
      setActiveTab(nextTab.id)
      focusTab(nextTab.id)
    },
    [focusTab],
  )

  return (
    <section
      className={`${styles.root}${raised ? ` ${styles.raised}` : ""}${flushTop ? ` ${styles.rootFlushTop}` : ""}`}
      aria-label="Suggested drugs"
    >
      <div
        className={styles.tabs}
        role="tablist"
        aria-label="Suggested drug categories"
      >
        {TABS.map((tab) => {
          const selected = activeTab === tab.id
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              id={`tracker-tab-${tab.id}`}
              aria-selected={selected}
              aria-controls={`tracker-panel-${tab.id}`}
              tabIndex={selected ? 0 : -1}
              className={`${styles.tab} ${selected ? styles.tabActive : ""}`}
              data-skip-focus-ring
              onClick={() => setActiveTab(tab.id)}
              onKeyDown={(event) => handleTabKeyDown(event, tab.id)}
            >
              {tab.label}
            </button>
          )
        })}
      </div>

      <div
        id={`tracker-panel-${activeTab}`}
        role="tabpanel"
        aria-labelledby={`tracker-tab-${activeTab}`}
        className={styles.grid}
      >
        {visibleDrugs.map((drug) => (
          <button
            key={`${activeTab}-${drug.generic}`}
            type="button"
            className={styles.row}
            data-skip-focus-ring
            aria-label={`Track ${drug.generic}, ${formatTrialCount(drug).toLowerCase()}`}
            onClick={() => void onSearchSelect(drug.generic)}
          >
            <span className={styles.glyph} aria-hidden>
              ↳
            </span>
            <span className={styles.nameBlock}>
              <span className={styles.name}>{drug.generic}</span>
              <span className={styles.indication}>{drug.indication}</span>
            </span>
            <span className={styles.count} aria-hidden>
              {formatTrialCount(drug)}
            </span>
          </button>
        ))}
      </div>

      <p className={styles.footer}>
        NIGHTLY SWEEP · 02:00 UTC · 11,000+ TRIALS CHECKED LAST NIGHT
      </p>
    </section>
  )
}
